import { useState, useEffect, useMemo } from 'react';
import { FolderKanban, CheckSquare } from 'lucide-react';
import { api } from '../../api/client';
import { useT } from '../../i18n';
import { FilterSelect } from '../../components/FilterSelect';
import { useRole } from '../../context/RoleContext';
import type { InvolvementItem, InvolvementType } from '../../types';
import { fmtShortDate } from '../../utils/date';

// [Proposition: implications] Gérance "Implications" tab — who was involved in
// which task (assignee, collaborator) or project (sponsor, PMO accompanist,
// collaborator, team member), by whom and when. Replaces the "you were
// assigned / added" notifications and e-mails: people check this table, and
// the PMO reviews it in the Monday meeting. Admins see everyone with a person
// filter; others only get their own rows (scoped by the server).

const TYPE_ICON: Record<InvolvementType, typeof FolderKanban> = {
  project: FolderKanban,
  task: CheckSquare,
};

function open(item: InvolvementItem) {
  window.location.hash = item.type === 'task' ? `tasks?task=${item.taskId}` : `project?id=${item.projectId}`;
}

export function GeranceInvolvementsView() {
  const { t } = useT();
  const { isAdmin } = useRole();
  const [items, setItems] = useState<InvolvementItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<InvolvementType | ''>('');
  const [personFilter, setPersonFilter] = useState('');

  useEffect(() => {
    api.getInvolvements()
      .then(res => setItems(res.items))
      .finally(() => setLoading(false));
  }, []);

  const persons = useMemo(
    () => [...new Set(items.map(i => i.person))].sort((a, b) => a.localeCompare(b)),
    [items]
  );

  const filtered = useMemo(() => items.filter(i =>
    (!typeFilter || i.type === typeFilter) && (!personFilter || i.person === personFilter)
  ), [items, typeFilter, personFilter]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const i of items) if (!personFilter || i.person === personFilter) counts[i.type] = (counts[i.type] || 0) + 1;
    return counts;
  }, [items, personFilter]);

  if (loading) return <div className="spinner" />;

  return (
    <>
      <p className="text-ink-3 mb-16">{isAdmin ? t('involvements.introAdmin') : t('involvements.intro')}</p>

      <div className="toolbar">
        <div className="filter-chips">
          <button className={`chip ${typeFilter === '' ? 'active' : ''}`} onClick={() => setTypeFilter('')}>
            {t('echeances.filterAllTypes')} ({(typeCounts.task || 0) + (typeCounts.project || 0)})
          </button>
          {(['task', 'project'] as InvolvementType[]).map(ty => (
            <button
              key={ty}
              className={`chip ${typeFilter === ty ? 'active' : ''}`}
              onClick={() => setTypeFilter(typeFilter === ty ? '' : ty)}
            >
              {t(`involvements.type.${ty}`)} ({typeCounts[ty] || 0})
            </button>
          ))}
        </div>

        {isAdmin && (
          <FilterSelect
            label={t('filters.person')}
            multiple={false}
            selected={personFilter ? [personFilter] : []}
            options={persons.map(p => ({ value: p, label: p }))}
            emptyLabel={t('tasks.everyone')}
            onChange={vals => setPersonFilter(vals[0] ?? '')}
          />
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">{t('involvements.emptyTitle')}</div>
          <div className="empty-state-desc">{t('involvements.emptyDesc')}</div>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 110 }}>{t('involvements.col.date')}</th>
              {isAdmin && <th>{t('involvements.col.person')}</th>}
              <th>{t('involvements.col.item')}</th>
              <th>{t('involvements.col.role')}</th>
              <th>{t('echeances.col.project')}</th>
              <th>{t('involvements.col.by')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(item => {
              const Icon = TYPE_ICON[item.type];
              return (
                <tr key={item.id} className="echeance-row" onClick={() => open(item)}>
                  <td><span className="echeance-date">{fmtShortDate(item.at)}</span></td>
                  {isAdmin && <td className="font-medium text-ink-1">{item.person}</td>}
                  <td>
                    <span className="echeance-type">
                      <Icon className="icon-sm icon-inline" />
                      {item.type === 'task' ? item.taskTitle : t('involvements.projectItem')}
                    </span>
                  </td>
                  <td className="text-ink-2">{item.role}</td>
                  <td>
                    <span className="badge badge-accent">{item.projectCode || item.projectName}</span>
                    {item.projectCode && <span className="text-xs text-ink-3" style={{ marginLeft: 'var(--s8)' }}>{item.projectName}</span>}
                  </td>
                  <td className="text-ink-2">{item.byName}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}
